/* eslint-disable no-param-reassign */
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import find from 'lodash/find';
import { findTaggedValue } from './tags/findTags';

export const getVanityPath = (props) => {
  const { id, shortLabel, tags } = props;

  let queryKey;
  if (tags.length > 0) {
    const sortedTags = [...tags].sort();

    queryKey = sortedTags.reduce((acc, tag) => {
      if (tag.includes('carpettype')) {
        return `${acc}carpettype`;
      }
      if (tag.includes('colorfamily')) {
        return `${acc}colorfamily`;
      }
      if (tag.includes('padPrice')) {
        return `${acc}pad`;
      }
      return acc;
    }, '');
  } else {
    queryKey = tags?.[0]?.replace(':', '-');
  }

  if (!queryKey) {
    return '';
  }
  return `${queryKey}=${shortLabel.replace(/[\W_]+/g, '-').replace(/^-+|-+$/g, '')}_${id}`;
};

const isParentSelected = (options, choices, option) => {
  if (!option.isDependent) {
    return true;
  }
  const choice = choices[option.parentChoiceId];
  if (choice && choice.isSelected) {
    const option2 = options[choice.optionId];
    return option2 && isParentSelected(options, choices, option2);
  }
  return false;
};

export const isMultiSelectOption = (options, id) => options[id].tags.includes('multi-choice-group');

export const isMultiSelectChoice = (choices, id) => choices[id].tags.includes('multi-choice-values');

const getSelectedChoice = (options, choices, optionId) => {
  const option = options[optionId];
  const selectedChoice =
    option &&
    option.isActive &&
    isParentSelected(options, choices, option) &&
    option.choices.find((cid) => choices[cid].isSelected);
  return selectedChoice;
};

const getMultiValueSelectedChoices = (options, choices, optionId) => {
  const option = options[optionId];
  const multiChoiceId = option.choices.find((id) => isMultiSelectChoice(choices, id));
  const { id, value } = choices[multiChoiceId];
  let colorNames = value;
  if (value === 'ChoiceValues' || value === '') {
    return '';
  }
  if (value.match(/^\d/)) { // is multiChoice value starting with a digit even possible?
    const dashIndex = value.indexOf('_');
    const pipeIndex = value.indexOf('|');
    if (dashIndex >= 0) colorNames = value.substring(dashIndex) + 1;
    if (pipeIndex >= 0) colorNames = value.substring(pipeIndex) + 1;
  }
  return `multiChoice=${id}_${colorNames.replace(/[\W_]+/g, ',')}`;
};

export const getChoicePaths = (options, choices) => {
  const multiSelectOptionId = options.allIds.find((id) => isMultiSelectOption(options, id));
  const selectedChoices = options.allIds.map((id) => {
    if (id === multiSelectOptionId) {
      return getMultiValueSelectedChoices(options, choices, multiSelectOptionId);
    }
    return getSelectedChoice(options, choices, id);
  }).filter((choice) => choice);

  const parts = selectedChoices.map((id) => {
    // there are some that are already configured in the options.allIds map and return the param in string already
    if (typeof id === 'string') { return `${id}`; }
    const vanityPath = getVanityPath(choices[id]);
    return vanityPath;
  });
  return parts;
};

const getFallbackPath = () => {
  const fallback = window.routeInfo.basePath.split('/', 3)[2]?.includes('-')
    ? window.location.pathname.split(/(.*?\d{6}|.*?\/flooring)(?:\/|$)/)
    : window.location.pathname.split(/(.*?\d{6})(?:\/|$)/);

  return fallback;
};

export const getPathFromSelectedChoices = (
  configurator,
  basePath = undefined
) => {
  let path = basePath;

  if (!path) {
    if (window.routeInfo && window.routeInfo.basePath) {
      path = window.routeInfo.basePath.split('/', 3)[2]?.includes('-')
        ? `${window.routeInfo.basePath.split('/', 2).join('/')}`
        : `${window.routeInfo.basePath.split('/', 3).join('/')}`;
    } else {
      [, path] = getFallbackPath();
    }
  }

  const { options, choices } = configurator;

  let pathFromSelections;
  if (path.includes('?')) {
    const choicePaths = getChoicePaths(options, choices).join('&');

    if (choicePaths?.length > 0) {
      pathFromSelections = `${path}&${choicePaths}`;
    } else {
      pathFromSelections = `${path}${choicePaths}`;
    }
  } else {
    pathFromSelections = `${path}?${getChoicePaths(options, choices).join(
      '&'
    )}`;
  }

  return pathFromSelections;
};

export const getChoicePathsIfSelected = (options, choices, choiceId) => {
  const selectedChoices = options.allIds
    .map((oid) => {
      const optionChoices = options[oid].choices;
      if (optionChoices.includes(choiceId)) {
        return choiceId;
      }

      return optionChoices.find((cid) => choices[cid].isSelected);
    })
    .filter((choice) => choice);
  const parts = selectedChoices
    .map((id) => getVanityPath(choices[id]))
    .filter((part) => part); // remove empty paths
  return parts;
};

export const getQueryArray = (path) => {
  return path?.split('?')?.[1] || undefined;
};

export const getPreconfiguration = (options, choices, choicePath = null) => {
  let path = getQueryArray(window.location.href);

  if (!path) {
    if (window.routeInfo && window.routeInfo.choicePath) {
      path = window.routeInfo.basePath.split('&', 3).join('&');
    } else {
      [, , path] = getFallbackPath();
    }
  }

  const parts = Array.isArray(path) ? path : (path || '').split('&');

  const partsFromPath = parts
    .map((part) => part.split('-', 1).map((n) => parseInt(n, 10)))
    .reduce((obj, [id]) => {
      if (!Number.isNaN(id) && id in choices) {
        obj[choices[id].optionId] = { choiceId: id };
      }

      return obj;
    }, {});

  return partsFromPath;
};

export const getSplitUrlQuery = (path) => {
  const queryArray = getQueryArray(path);
  return queryArray ? queryArray.split('&') : [];
};

export const isNucleusEnvironment = () => {
  return window.location.pathname.indexOf('render-nucleus') > 0;
};

export const updateRoomsPath = (roomMeasures) => {
  if (!isEmpty(roomMeasures)) {
    const rooms = roomMeasures.map((room) => `${room.room}-${room.length}-${room.width}-${room.sqFt}`);
    return rooms.join(',');
  }
  return '';
};

export const updateStepsPath = (steps) => {
  // TODO the concept of Yes/No needs to be phased out with new configurator design
  // there is no longer a steps question of yes or no
  const { questionAnswered, steps: step, stairsSquareFeet } = steps;
  const hasStepSelection = questionAnswered ? `${steps.steps > 0 ? 'Yes' : 'No'}` : null;
  const hasStepsInfo = hasStepSelection && step >= 0 && stairsSquareFeet >= 0;
  return hasStepsInfo
    ? `${hasStepSelection}-${steps.steps || 0}-${steps.stairsSquareFeet || 0}`
    : '';
};

export const getSelectedPadFromConfigurator = (options, choices) => {
  const foundCarpetPadOption = filter(options, (op) => op.tags?.includes('carpet:padOption'));
  if (!isEmpty(foundCarpetPadOption)) {
    const carpetPadChoiceIds = foundCarpetPadOption[0]?.choices;
    if (!isEmpty(carpetPadChoiceIds)) {
      // map over choiceIds and find the pad choices in configurator choices
      return filter(carpetPadChoiceIds.map((padChoice) => choices[padChoice]), (choice) => choice.isSelected)[0];
    }
  }
  return [];
};

export const updateCarpetPadPath = (selectedCarpetPad) => {
  return !isEmpty(selectedCarpetPad) ? `${selectedCarpetPad.internalName}_${selectedCarpetPad.id}` : '';
};

export const updateCarpetPath = (choices) => {
  if (!isEmpty(choices)) {
    const selectedCarpet = find(choices, (choice) => choice.isSelected);
    if (!isEmpty(selectedCarpet)) {
      return `${selectedCarpet.shortLabel.replaceAll(' ', '_')}_${selectedCarpet.id}`;
    }
  }
  return '';
};

export const updateOmsIdPath = (choices, omsId) => {
  if (!omsId) { return ''; } // don't add omsId to non-ssku experience
  if (!isEmpty(choices)) {
    const selectedCarpet = find(choices, (choice) => choice.isSelected);
    if (selectedCarpet) {
      const sku = omsId || findTaggedValue(selectedCarpet?.tags, 'sku');
      return `${sku}`;
    }
  }
  return '';
};